/**
 * useContentStrategy — real-time subscription for a single strategy doc.
 * Uses ContentStrategyFirestoreService.subscribeStrategy().
 */

import { useEffect, useState } from 'react';
import { ContentStrategyFirestoreService } from '../services/ContentStrategyFirestoreService';
import type { ContentStrategy, ContentSprint } from '../types';

export interface UseContentStrategyReturn {
  strategy: ContentStrategy | null;
  sprints: ContentSprint[];
  loading: boolean;
  error: Error | null;
}

let sharedService: ContentStrategyFirestoreService | null = null;
function getService() {
  if (!sharedService) sharedService = new ContentStrategyFirestoreService();
  return sharedService;
}

export function useContentStrategy(
  projectId: string | null,
  strategyId: string | null,
): UseContentStrategyReturn {
  const [strategy, setStrategy] = useState<ContentStrategy | null>(null);
  const [sprints, setSprints] = useState<ContentSprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !strategyId) {
      setStrategy(null);
      setSprints([]);
      setLoading(false);
      return;
    }
    const svc = getService();
    let cancelled = false;
    setLoading(true);
    setError(null);

    const unsubStrategy = svc.subscribeStrategy(projectId, strategyId, (s) => {
      if (cancelled) return;
      setStrategy(s);
      setLoading(false);
    });

    const unsubSprints = svc.subscribeSprints(projectId, strategyId, (arr) => {
      if (cancelled) return;
      setSprints(arr);
    });

    return () => {
      cancelled = true;
      unsubStrategy();
      unsubSprints();
    };
  }, [projectId, strategyId]);

  return { strategy, sprints, loading, error };
}
